
import React, { useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import BrowseSidebar from './BrowseSidebar';
import BrowseGrid from './BrowseGrid';
import BrowseTagFilter from './BrowseTagFilter';
import { Story } from '@/utils/storiesData';
import SearchInput from '@/components/Search/SearchInput';
import { useNavigate } from 'react-router-dom';

interface BrowseDesktopLayoutProps {
  filteredStories: Story[];
  categories: string[];
  activeCategory: string;
  onCategoryChange: (category: string) => void;
  categoryCounts: Record<string, number>;
  tags: string[];
  activeTag: string | null;
  onTagChange: (tag: string | null) => void;
  activeCategoryDescription?: string;
}

const BrowseDesktopLayout: React.FC<BrowseDesktopLayoutProps> = ({
  filteredStories,
  categories,
  activeCategory,
  onCategoryChange,
  categoryCounts,
  tags,
  activeTag,
  onTagChange,
  activeCategoryDescription = ''
}) => {
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  
  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';
  const mutedTextColor = resolvedTheme === 'dark' ? 'text-white/70' : 'text-black/70';
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  
  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };
  
  const handleClearSearch = () => {
    setSearchQuery('');
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  return (
    <div className="flex flex-col md:flex-row gap-8 relative">
      <div className="w-full md:w-64 shrink-0">
        <div className="mb-6">
          <SearchInput
            query={searchQuery}
            onChange={handleSearchChange}
            onClear={handleClearSearch}
            onKeyDown={handleKeyDown}
          />
        </div>
        
        <BrowseSidebar 
          categories={categories} 
          activeCategory={activeCategory} 
          onCategoryChange={onCategoryChange}
          categoryCounts={categoryCounts}
        />
        
        {tags.length > 0 && (
          <div className="mt-8">
            <BrowseTagFilter
              tags={tags}
              activeTag={activeTag}
              onTagChange={onTagChange}
            />
          </div>
        )}
      </div>
      
      <div className="flex-1">
        <h2 className={cn("text-2xl font-chroma font-normal mb-2 capitalize", textColor)}>
          {activeCategory === 'interior-design' ? 'Interiors' : activeCategory}
        </h2>
        
        {activeCategoryDescription && (
          <p className={cn("text-base mb-6", mutedTextColor)}>
            {activeCategoryDescription}
          </p>
        )}
        
        {activeTag && (
          <div className="mb-6">
            <p className={cn("text-sm font-medium", mutedTextColor)}>
              Filtered by tag: <span className={cn("font-bold capitalize", textColor)}>{activeTag}</span>
            </p>
          </div>
        )}
        
        <BrowseGrid stories={filteredStories} />
      </div>
    </div>
  );
};

export default BrowseDesktopLayout;
